import React, { useState, useEffect, useRef } from 'react';
import { usePracticeContent } from '../hooks/usePracticeContent';

const REDUCED =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Same count-up the hero/disciplines use: 0 → target, no flash of the final
// value, static under prefers-reduced-motion. Driven by `ready` (in-view + loaded).
function CountUp({ value, suffix, ready }) {
  const [n, setN] = useState(REDUCED ? value : 0);
  useEffect(() => {
    if (REDUCED) { setN(value); return undefined; }
    if (!ready) { setN(0); return undefined; }
    let raf;
    const dur = 1400;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setN(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready, value]);
  return (
    <>
      {n.toLocaleString('en-US')}
      {suffix}
    </>
  );
}

const PLACE_1 = 'linear-gradient(180deg,#DEDFD6,#C2C8B6 46%,#8B9A7C 74%,#54644B)';
const PLACE_2 = 'linear-gradient(180deg,#E9ECE5,#D8DFD1 45%,#AEBFA2 72%,#7E9372)';

const PracticePrime = () => {
  const { data, loading } = usePracticeContent();
  // read defensively so a fetch-fallback payload (no prime) never breaks the scene
  const prime = (data && data.prime) || {
    plants: { value: 700, suffix: '+' },
    parameters: { value: 50, suffix: '+' },
  };
  const images = (data && data.images) || {};
  const p1 = images.prime_1 || { url: '', caption: '' };
  const p2 = images.prime_2 || { url: '', caption: '' };

  const [inView, setInView] = useState(!!REDUCED);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (REDUCED) return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } });
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const ready = inView && !loading;

  // the punchline: static ∞ fades in only AFTER the two count-ups finish
  const [infIn, setInfIn] = useState(!!REDUCED);
  useEffect(() => {
    if (REDUCED) { setInfIn(true); return undefined; }
    if (!ready) { setInfIn(false); return undefined; }
    const t = setTimeout(() => setInfIn(true), 1500);
    return () => clearTimeout(t);
  }, [ready]);

  const rev = (delay = 0) => ({
    className: `prime-rev${inView ? ' in' : ''}`,
    style: { transitionDelay: `${delay}ms` },
  });

  const cellStyle = (first) => ({
    flex: '1 1 0%',
    minWidth: 220,
    padding: first ? '44px 40px 34px 0' : '44px 40px 34px',
    borderLeft: first ? 'none' : '1px solid rgba(255,255,255,.15)',
  });
  const bigNum = { fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 'clamp(64px,9vw,140px)', letterSpacing: '-.05em', lineHeight: 1, color: '#fff', fontVariantNumeric: 'tabular-nums' };
  const numLabel = { fontFamily: 'Inter, sans-serif', fontSize: '12.5px', letterSpacing: '.24em', marginTop: 16, color: '#a9c3e4' };

  return (
    <section ref={sectionRef} className="relative" style={{ background: '#002F6C', color: '#fff', padding: '160px 0' }} data-testid="practice-prime-section">
      <style>{`
        .prime-rev{ opacity:0; transform:translateY(16px); transition:opacity .7s cubic-bezier(0.2,0.8,0.2,1), transform .7s cubic-bezier(0.2,0.8,0.2,1); }
        .prime-rev.in{ opacity:1; transform:none; }
        .prime-word{ display:flex; justify-content:space-between; align-items:baseline;
          width:100%; max-width:860px; margin-top:18px; letter-spacing:normal;
          font-family:Inter, sans-serif; font-weight:100; font-size:clamp(64px,14vw,210px); line-height:1; color:#fff; }
        .prime-inf{ opacity:0; transition:opacity .8s ease; }
        .prime-inf.in{ opacity:1; }
        .pduo{ position:relative; display:grid; grid-template-columns:1fr 1fr; gap:2px; margin-top:90px; }
        .pseam{ position:absolute; left:50%; top:0; bottom:0; width:1px; background:rgba(255,255,255,.35); pointer-events:none; }
        .ptile-2{ transform:translateY(40px); }
        .ptile .pcap{ opacity:0; transition:opacity .3s ease; }
        .ptile:hover .pcap{ opacity:1; }
        @media (max-width:760px){ .pduo{ grid-template-columns:1fr; } .ptile-2{ transform:none; } .pseam{ display:none; } }
        @media (prefers-reduced-motion: reduce){ .prime-rev{ opacity:1; transform:none; transition:none; } .prime-inf{ opacity:1; transition:none; } }
      `}</style>

      {/* decorative dot texture */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.14, backgroundImage: 'radial-gradient(rgba(255,255,255,.7) 1px, transparent 1.6px)', backgroundSize: '34px 34px' }} />

      <div className="pr-spine" style={{ position: 'relative' }}>
        <p {...rev(0)} style={{ ...rev(0).style, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '11.5px', letterSpacing: '.3em', color: '#8fb3e0' }} data-testid="practice-prime-eyebrow">
          our research, since day one
        </p>

        <div {...rev(80)} className={`prime-word prime-rev${inView ? ' in' : ''}`} style={{ transitionDelay: '80ms' }} data-testid="practice-prime-wordmark">
          {['P', 'R', 'I', 'M', 'E'].map((l) => (<span key={l}>{l}</span>))}
        </div>

        <p {...rev(160)} style={{ ...rev(160).style, fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontSize: 'clamp(20px,2.6vw,30px)', color: '#cfe0f4', maxWidth: 640, marginTop: 26 }} data-testid="practice-prime-sub">
          what it is, we will show you on site. what it knows is the point:
        </p>

        {/* numbers row */}
        <div {...rev(220)} className={`pnums prime-rev${inView ? ' in' : ''}`} style={{ transitionDelay: '220ms', display: 'flex', flexWrap: 'wrap', margin: '90px 0 70px', borderTop: '1px solid rgba(255,255,255,.18)' }} data-testid="practice-prime-numbers">
          <div style={cellStyle(true)}>
            <div style={bigNum} data-testid="practice-prime-plants"><CountUp value={prime.plants.value} suffix={prime.plants.suffix} ready={ready} /></div>
            <div style={numLabel}>PLANTS STUDIED</div>
          </div>
          <div style={cellStyle(false)}>
            <div style={bigNum} data-testid="practice-prime-parameters"><CountUp value={prime.parameters.value} suffix={prime.parameters.suffix} ready={ready} /></div>
            <div style={numLabel}>PARAMETERS PER PLANT</div>
          </div>
          <div style={cellStyle(false)}>
            <div style={bigNum} className={`prime-inf${infIn ? ' in' : ''}`} data-testid="practice-prime-infinity">∞</div>
            <div style={numLabel}>THE RESEARCH IS STILL ON</div>
          </div>
        </div>

        <p {...rev(120)} style={{ ...rev(120).style, fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontSize: 'clamp(22px,3vw,34px)', color: '#cfe0f4', maxWidth: 760, lineHeight: 1.5 }} data-testid="practice-prime-closer">
          life cycle, maintenance, water, soil — everything a plant will ask of a site, recorded before we plant it. zone by zone, across the country. every planting decision at studio 2+2 passes through it.
        </p>

        {/* image duo */}
        <div className="pduo" data-testid="practice-prime-duo">
          <div className="pseam" aria-hidden="true" data-testid="practice-prime-seam" />
          {[[p1, PLACE_1, 'prime-1'], [p2, PLACE_2, 'prime-2']].map(([img, placeholder, key], idx) => (
            <div key={key} className={`ptile${idx === 1 ? ' ptile-2' : ''}`} style={{ position: 'relative', height: 380, overflow: 'hidden', background: img.url ? '#000' : placeholder }} data-testid={`practice-${key}-tile`}>
              {img.url && (
                <img src={img.url} alt={img.caption || 'prime research'} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              )}
              <span className="pcap" style={{ position: 'absolute', left: 16, bottom: 14, fontFamily: 'Inter, sans-serif', fontSize: '10.5px', letterSpacing: '.14em', color: '#F8F8F7', textShadow: '0 1px 8px rgba(20,30,20,.5)' }}>
                {img.caption ? img.caption : 'a living site · your photograph'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PracticePrime;
